import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, Target, Shield, BookOpen, UserCircle, Menu, X, Calendar, FileText, Mail, Clock, Trophy, Bot, UserCheck, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '@/lib/utils';

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, hasRole } = useAuth();

  // Reorganized by step-by-step workflow process
  const navSections = [
    {
      title: 'Daily Activities',
      items: [
        { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: null },
        { to: '/pipeline', icon: Target, label: 'Prospect Pipeline', roles: null },
        { to: '/appointments', icon: Mail, label: 'Appointments', roles: null },
        { to: '/time-blocking', icon: Clock, label: 'Time Blocking', roles: null },
      ]
    },
    {
      title: 'Tools & Resources',
      items: [
        { to: '/playbook', icon: FileText, label: 'Scripts & Playbook', roles: null },
        { to: '/ai-assistant', icon: Bot, label: 'AI Coach', roles: null },
        { to: '/calendar', icon: Calendar, label: 'Content Planner', roles: null },
      ]
    },
    {
      title: 'Growth & Goals',
      items: [
        { to: '/planner', icon: BookOpen, label: 'Fast Start Planner', roles: null },
        { to: '/leaderboard', icon: Trophy, label: 'Leaderboard', roles: null },
        { to: '/profile', icon: UserCircle, label: 'My Profile', roles: null },
      ]
    },
    {
      title: 'Admin',
      items: [
        { to: '/team', icon: Users, label: 'Team Overview', roles: ['Admin', 'Regional_Vice_President', 'Senior_Regional_Leader'], showForTeamLeaders: true },
        { to: '/approvals', icon: UserCheck, label: 'Team Approvals', roles: ['Admin'], showForTeamLeaders: true },
        { to: '/manage', icon: Shield, label: 'Role Management', roles: ['Admin'] },
        { to: '/admin/tenants', icon: Building2, label: 'All Teams (SaaS)', roles: ['Admin'] },
      ]
    }
  ];

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-[#333333] text-white transition-all duration-300",
          collapsed ? "-translate-x-full lg:translate-x-0 lg:w-16" : "translate-x-0 w-64"
        )}
      >
        {/* Logo/Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src="/peacock-logo.png" alt="Logo" className="h-10 w-10 object-contain" />
              <h1 className="text-lg font-bold text-[#E31837]">VRMX</h1>
            </div>
          )}
          {collapsed && (
            <img src="/peacock-logo.png" alt="Logo" className="h-8 w-8 object-contain mx-auto" />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 hover:bg-white/10 rounded-md transition-colors lg:block hidden"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto scrollbar-hide">
          {navSections.map((section, sectionIndex) => {
            const visibleItems = section.items.filter(item => {
              // Show items with no role restriction
              if (!item.roles) return true;
              // Show if user has the required role
              if (hasRole(item.roles)) return true;
              // Show for team leaders if item has showForTeamLeaders flag
              if (item.showForTeamLeaders && user?.is_team_leader) return true;
              return false;
            });
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={section.title}>
                {/* Section Title */}
                {!collapsed && (
                  <div className="px-4 py-2 mt-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {section.title}
                    </p>
                  </div>
                )}
                {collapsed && sectionIndex > 0 && (
                  <div className="mx-3 my-2 border-t border-white/10" />
                )}
                
                {/* Section Items */}
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        setCollapsed(true);
                      }
                    }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center px-4 py-2.5 transition-colors",
                        collapsed ? "justify-center" : "gap-3",
                        isActive
                          ? "bg-[#E31837] text-white"
                          : "text-gray-300 hover:bg-white/10 hover:text-white"
                      )
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        
        {/* Motivational Quote at bottom */}
        {!collapsed && (
          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-gray-400 italic text-center">
              "Today's effort is tomorrow's success."
            </p>
          </div>
        )}
      </aside>
    </>
  );
};
